import React from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { Smartphone, Cpu, Satellite, RadioTower, Award, Globe, GraduationCap } from "lucide-react";

const fade = {
  hidden: { opacity: 0, y: 20 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.08, duration: 0.5, ease: "easeOut" },
  }),
};

const calculateDuration = (startDate, endDate = new Date()) => {
  const start = new Date(startDate);
  const end = endDate === "current" ? new Date() : new Date(endDate);

  let years = end.getFullYear() - start.getFullYear();
  let months = end.getMonth() - start.getMonth();

  if (months < 0) {
    years--;
    months += 12;
  }

  return `${years} yrs ${months} mos`;
};

const stats = [
  {
    icons: [Satellite, RadioTower],
    value: "Satellite NTN",
    label: "RAN development",
    sub: "Skylo · 5G non-terrestrial",
  },
  {
    icon: Cpu,
    value: "5G",
    label: "Modem development",
    sub: "Sub-6 & mmWave · Samsung",
  },
  {
    icon: GraduationCap,
    value: "M.S.",
    label: "Electrical Engineering",
    sub: "University at Buffalo (SUNY)",
  },
  {
    icon: Smartphone,
    value: "18M+",
    label: "Devices connected",
    sub: "Skylo D2D, scaled from zero",
  },
  {
    icon: Award,
    value: "16",
    label: "Patents + paper publications",
    sub: "10 patent families · 6 publications",
    to: "/publications",
  },
  {
    icon: Globe,
    value: "3GPP",
    label: "Standards contributions",
    sub: "RAN2 / RAN4 · CT1 / SA",
    to: "/projects/satellite-comms/3gpp-standards-work",
  },
];

const roles = [
  {
    title: "Staff Wireless Systems Engineer, CTO Team",
    org: "Skylo Technologies",
    badge: "Early member",
    period: `Apr 2022 – Present · ${calculateDuration("2022-04-01", "current")}`,
    summary:
      "An early engineer at a satellite-connectivity startup, scaling from 0 to 18M+ devices delivering global Emergency SOS. I work across R&D, customer engineering, and partner integration to bring complex wireless systems into production.",
    bullets: [
      "Designed and deployed a dynamic power and resource-allocation algorithm on a live satellite-partner network, restoring link closure by 3 dB and yielding a ~20% coverage gain.",
      "Led novel channel-estimation and preamble-detection algorithms for NB-IoT and 5G NTN.",
      "Technical lead for OEM partners Samsung Galaxy and Garmin; drove Pixel 9, Pixel 10, and Pixel Watch NTN integration across the network.",
      "Own end-to-end link-budget analysis across satellites for mobile and IoT devices, from theory through lab and field testing.",
      "Driving a new NR LEO offering and satellite payload design; competitive analysis of modems, beamformer ICs, FPGA/ASIC suppliers, and satellite manufacturers.",
      "Prototyping AI-RAN for 5G channel estimation, voice over GEO with NB-NTN, and ISAC using 5G waveforms.",
      "3GPP delegate for Skylo: authored and defended contributions across RAN2, RAN4, CT1 and SA on IoT-NTN, from voice over Non-IP and emergency prioritisation to diversity slotted ALOHA in Rel-19.",
    ],
    tags: ["5G NTN", "NB-IoT", "LEO / GEO", "3GPP", "Link budget", "AI-RAN"],
  },
  {
    title: "Research Volunteer",
    org: "Stanford University · Hopkins Marine Station",
    badge: "Research",
    period: `Feb 2025 – Present · ${calculateDuration("2025-02-01", "current")}`,
    summary:
      "GNSS and positioning research for marine animals, rapid-fix, multi-band, sensor-fusion localization for animals that surface only 2–3 seconds at a time.",
    bullets: [
      "Hands-on GNSS modem evaluation with u-blox F9 and F10N (L1 + L5): multi-band acquisition, time-to-first-fix, and snapshot-positioning accuracy.",
      "Faster-fix techniques, Assisted-GNSS, RTK, Kalman-filter sensor fusion, and cellular-aided positioning, with differential-correction prototypes.",
      "LEO signals-of-opportunity positioning (Doppler-based pseudorange via SDR) for low-latency localization (IEEE RADARCONF 2026).",
    ],
    tags: ["GNSS", "u-blox F9 / F10", "RTK", "Sensor fusion", "SDR"],
  },
  {
    title: "Senior Engineer, Cellular SoC",
    org: "Samsung Semiconductor",
    period: "Oct 2019 – Apr 2022 · 2 yrs 6 mos",
    summary:
      "Physical-layer modem developer and customer-engineering lead for uplink power control and SAR. As the customer-facing technical lead for Google Pixel 7 and 8, I built power-efficient 5G modems working across both sub-6 GHz and mmWave frequencies.",
    bullets: [
      "Maximum-transmit-power, dynamic power-sharing (LTE↔NR, FR1/FR2), and SAR / power-density algorithms in C/C++, balancing performance, FCC limits, and battery life.",
      "LTE transmit antenna switching/selection to maximize link budget across uplink carrier-aggregation scenarios.",
      "Optimized SAR and power-sharing via MATLAB simulations with RF Systems; validated and debugged on Keysight and Anritsu equipment.",
      "Diagnosed uplink-power and RACH issues via UE log analysis against 3GPP specs, fixing physical-layer bugs in production firmware.",
    ],
    tags: ["5G NR", "Sub-6 & mmWave", "SAR", "C / C++", "Power sharing"],
  },
  {
    title: "Graduate Research Assistant",
    org: "University at Buffalo (SUNY)",
    badge: "Research",
    period: `May 2018 – Sep 2019 · ${calculateDuration("2018-05-01", "2019-09-01")}`,
    summary:
      "Where it started, proving robust underwater MIMO outside of simulation, using hardware I built myself.",
    bullets: [
      "Built a hybrid underwater distributed-MIMO testbed (acoustic + magnetic induction) on USRP N210 / GNU Radio / MATLAB; implemented beamforming and Alamouti coding, analyzing SNR, BER, and throughput.",
      "Implemented an end-to-end OFDM transceiver (FFT/IFFT, 4-QAM) in Verilog on a DE2i-150 FPGA.",
    ],
    tags: ["Underwater MIMO", "Beamforming", "USRP N210", "Verilog / FPGA"],
  },
  {
    title: "Graduate Lab Assistant",
    org: "Electronics Tinkering Lab",
    period: "Aug 2018 – Aug 2019 · 1 yr",
    summary:
      "Ran the undergraduate electronics lab, teaching students to actually use their tools and build real hardware.",
    bullets: [
      "Taught lab instruments hands-on: oscilloscopes, spectrum analyzers, and soldering.",
      "Wrote IoT teaching modules so students could learn to build embedded devices end to end.",
    ],
    tags: ["Teaching", "Embedded", "IoT"],
  },
  {
    title: "Co-Founder",
    org: "Airolyzr · Incubated at RIIDL",
    period: "Mar 2014 – Feb 2016 · 1 yr 11 mos",
    summary:
      "My first company, co-founded while finishing my Electronics bachelor's. We built an IoT air-pollution measuring device aimed at India's rising pollution problem, incubated at RIIDL. It didn't scale, but it set the instinct to ship hardware that works in the real world.",
    bullets: [],
    tags: ["IoT", "Air quality", "Hardware", "Startup"],
  },
];

const skills = [
  {
    group: "Cellular & 3GPP",
    items: [
      "5G NR (FR1/FR2 mmWave)",
      "LTE",
      "NB-IoT",
      "NTN",
      "PHY / MAC",
      "RAN & Core",
      "3GPP contributions (RAN2/4, CT1, SA)",
    ],
  },
  {
    group: "Satellite & GNSS positioning",
    items: [
      "LEO / GEO direct-to-device",
      "u-blox F9 / F10 (L1, L1+L5)",
      "Assisted-GNSS",
      "RTK",
      "Kalman / sensor fusion",
      "Cellular-aided positioning",
    ],
  },
  {
    group: "Chipsets & modules",
    items: [
      "Qualcomm Snapdragon / X85",
      "MediaTek M90 / T900",
      "Samsung Exynos",
      "Altair ALT1350",
      "Beamformer ICs",
      "AMD Zynq FPGA",
      "Nvidia L4 GPU",
    ],
  },
  {
    group: "RF, test & measurement",
    items: [
      "SDR (USRP)",
      "Spectrum / network / signal analyzers",
      "Oscilloscopes",
      "Keysight / R&S / Anritsu",
      "QXDM · Wireshark",
      "UE & eNB log analysis",
    ],
  },
  {
    group: "Programming",
    items: ["Python", "C", "C++", "MATLAB", "Verilog"],
  },
];

const education = [
  {
    school: "Stanford University – Continuing Studies",
    degree: "Professional education",
    detail:
      "AI and machine learning for business (Python), innovation and creativity, communication with impact.",
  },
  {
    school: "University at Buffalo (SUNY)",
    degree: "M.S., Electrical Engineering",
    detail:
      "Coursework in 5G/LTE & extreme-environment wireless, cellular networks, MIMO, IoT, and modern digital communication. Thesis on underwater distributed MIMO; earned the Dean's Award.",
  },
  {
    school: "K.J. Somaiya College of Engineering",
    degree: "B.E., Electronics Engineering",
    detail:
      "Where the interest in building started. Co-founded Airolyzr, an IoT air-quality startup incubated at RIIDL, during the final years.",
  },
];

const pageTitleClass =
  "font-display text-3xl md:text-4xl font-semibold text-foreground border-l-2 border-accent pl-4";

function StatCard({ stat }) {
  const icons = stat.icons || (stat.icon ? [stat.icon] : []);
  const inner = (
    <>
      <div className="flex items-center gap-1.5 mb-3">
        {icons.map((Ic, i) => (
          <Ic key={i} className="w-5 h-5 text-accent" />
        ))}
      </div>
      <div className="font-display text-3xl md:text-4xl font-semibold text-foreground leading-none">
        {stat.value}
      </div>
      <div className="mt-2 text-sm font-medium text-foreground">{stat.label}</div>
      <div className="mt-0.5 text-xs text-muted-foreground">{stat.sub}</div>
    </>
  );
  const base =
    "block rounded-lg border border-border/60 bg-surface/40 p-5 transition-colors";
  return stat.to ? (
    <Link to={stat.to} className={`${base} hover:border-accent/40 hover:bg-surface`}>
      {inner}
    </Link>
  ) : (
    <div className={base}>{inner}</div>
  );
}

export default function EducationExperience() {
  return (
    <div className="flex flex-col px-4 md:px-8">
      <Helmet>
        <title>Education &amp; Experience | Soham Desai</title>
        <meta name="description" content="Soham Desai's journey through eight years in wireless: from co-founding an IoT air-quality startup to power-efficient 5G modems at Samsung and an early role building Skylo's direct-to-device satellite network." />
      </Helmet>

      <motion.div
        initial="hidden"
        animate="visible"
        variants={{ visible: { transition: { staggerChildren: 0.08 } } }}
        className="max-w-4xl mx-auto w-full flex-1 py-10 md:py-14 space-y-14"
      >
        <motion.h1 variants={fade} className={pageTitleClass}>
          Education &amp; Experience
        </motion.h1>

        {/* ── Stats breakdown ── */}
        <motion.div
          variants={{ visible: { transition: { staggerChildren: 0.08 } } }}
          className="grid grid-cols-2 sm:grid-cols-3 gap-4"
        >
          {stats.map((stat) => (
            <motion.div key={stat.label} variants={fade}>
              <StatCard stat={stat} />
            </motion.div>
          ))}
        </motion.div>

        {/* ── Narrative intro ── */}
        <motion.p variants={fade} className="max-w-prose text-muted leading-relaxed text-base md:text-lg">
          Eight years in wireless, from 5G SoC modem design at Samsung to
          building one of the largest satellite direct-to-device networks at
          Skylo. My work has never been about a single technology, I like taking
          hard hardware-and-systems problems from a blank bench to something that
          ships at scale: power-efficient 5G modems, satellites that reach phones
          with no tower in sight, GNSS positioning, underwater radios, and IoT
          sensors.
        </motion.p>

        {/* ── Career timeline ── */}
        <motion.section
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-60px" }}
          variants={{ visible: { transition: { staggerChildren: 0.1 } } }}
        >
          <motion.h2
            variants={fade}
            className="font-display text-2xl font-semibold text-foreground mb-8"
          >
            Experience
          </motion.h2>

          <div className="relative ml-3 border-l border-border/70 pl-8 space-y-8">
            {roles.map((role) => (
              <motion.div key={role.title + role.org} variants={fade} className="relative">
                <span
                  className="absolute -left-[2.375rem] top-1.5 h-3 w-3 rounded-full bg-accent ring-4 ring-background"
                  aria-hidden="true"
                />
                <div className="rounded-lg border border-border/60 bg-surface/40 p-5 md:p-6 hover:border-accent/30 transition-colors">
                  <div className="flex flex-wrap items-start justify-between gap-x-3 gap-y-1">
                    <div>
                      <h3 className="text-base md:text-lg font-semibold text-foreground">
                        {role.title}
                      </h3>
                      <p className="text-sm text-accent">{role.org}</p>
                    </div>
                    {role.badge && (
                      <span className="shrink-0 px-2.5 py-0.5 rounded-full bg-accent/10 text-accent text-[0.7rem] tracking-wide uppercase border border-accent/20">
                        {role.badge}
                      </span>
                    )}
                  </div>

                  <p className="mt-1.5 text-xs uppercase tracking-wide text-muted-foreground">
                    {role.period}
                  </p>

                  {role.summary && (
                    <p className="mt-3 text-sm text-muted leading-relaxed">{role.summary}</p>
                  )}

                  {role.bullets.length > 0 && (
                    <ul className="mt-3 list-disc list-outside pl-5 space-y-1.5 text-sm text-muted leading-relaxed">
                      {role.bullets.map((b) => (
                        <li key={b}>{b}</li>
                      ))}
                    </ul>
                  )}

                  {role.tags.length > 0 && (
                    <div className="mt-4 flex flex-wrap gap-2">
                      {role.tags.map((t) => (
                        <span
                          key={t}
                          className="px-2.5 py-1 rounded-full bg-surface-elevated text-muted-foreground text-xs border border-border"
                        >
                          {t}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        </motion.section>

        {/* ── Technical skills ── */}
        <motion.section
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-60px" }}
          variants={{ visible: { transition: { staggerChildren: 0.08 } } }}
        >
          <motion.h2
            variants={fade}
            className="font-display text-2xl font-semibold text-foreground mb-8"
          >
            Technical skills
          </motion.h2>

          <div className="space-y-6">
            {skills.map((g) => (
              <motion.div key={g.group} variants={fade}>
                <h3 className="text-sm font-semibold text-foreground mb-2.5">{g.group}</h3>
                <div className="flex flex-wrap gap-2">
                  {g.items.map((item) => (
                    <span
                      key={item}
                      className="px-2.5 py-1 rounded-full bg-surface-elevated text-muted-foreground text-xs border border-border"
                    >
                      {item}
                    </span>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </motion.section>

        {/* ── Education ── */}
        <motion.section
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-60px" }}
          variants={{ visible: { transition: { staggerChildren: 0.1 } } }}
        >
          <motion.h2
            variants={fade}
            className="font-display text-2xl font-semibold text-foreground mb-8"
          >
            Education
          </motion.h2>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {education.map((e) => (
              <motion.div
                key={e.school}
                variants={fade}
                className="rounded-lg border border-border/60 bg-surface/40 p-5 hover:border-accent/30 transition-colors"
              >
                <GraduationCap className="w-5 h-5 text-accent mb-3" />
                <h3 className="text-sm font-semibold text-foreground leading-snug">{e.school}</h3>
                <p className="mt-1 text-xs uppercase tracking-wide text-muted-foreground">
                  {e.degree}
                </p>
                <p className="mt-2 text-sm text-muted leading-relaxed">{e.detail}</p>
              </motion.div>
            ))}
          </div>
        </motion.section>
      </motion.div>
    </div>
  );
}
